import { action, computed, observable, reaction } from 'mobx';

import { Time } from 'services/base/time';
import { ResultKind } from 'services/base/types';
import { NumberStore, StringStore } from 'ui/base/input';
import { IPangolinDomain, HostMap, IssuesFilter } from 'services/pangolin/types';
import { IssuesService, IssuesFilterApplyResult } from 'services/pangolin/issues_service';

import { CalendarRangeInputStore } from 'ui/standard/input/calendar_input';
import { ClockInputStore } from 'ui/standard/input/clock_input';

import { SeenStore } from 'ui/issues/seen_input';
import { KindInputStore } from 'ui/issues/kind_input';
import { DeviceInputStore } from 'ui/issues/device_input';

import Messages from './filter_issue_messages';

export class FilterIssueStore {
  private readonly filter: IssuesFilter;
  search: StringStore;
  dateRange: CalendarRangeInputStore;
  timeStart: ClockInputStore;
  timeEnd: ClockInputStore;
  seen: SeenStore;
  severityMin: NumberStore;
  severityMax: NumberStore;
  affectedMin: NumberStore;
  affectedMax: NumberStore;
  incidentsMin: NumberStore;
  incidentsMax: NumberStore;
  kinds: KindInputStore;
  devices: DeviceInputStore;

  @observable.ref
  seenError?: string;

  @observable.ref
  affectedError?: string;

  @observable.ref
  incidentsError?: string;

  cleanStartDateTime: () => void;
  cleanEndDateTime: () => void;

  constructor(filter: IssuesFilter, hosts: HostMap) {
    const dateStart = new Date(
      filter.start.getFullYear(),
      filter.start.getMonth(),
      filter.start.getDate()
    );
    const dateEnd = new Date(
      filter.end.getFullYear(),
      filter.end.getMonth(),
      filter.end.getDate()
    );
    const timeStart = new Time(
      filter.start.getHours(),
      filter.start.getMinutes(),
      filter.start.getSeconds()
    );
    const timeEnd = new Time(
      filter.end.getHours(),
      filter.end.getMinutes(),
      filter.end.getSeconds()
    );

    this.filter = filter;

    this.search = new StringStore(filter.search);
    this.dateRange = new CalendarRangeInputStore(dateStart, dateEnd);
    this.timeStart = new ClockInputStore(timeStart);
    this.timeEnd = new ClockInputStore(timeEnd);
    this.seen = new SeenStore(filter.seen);
    this.severityMin = new NumberStore(filter.severityMin);
    this.severityMax = new NumberStore(filter.severityMax);
    this.affectedMin = new NumberStore(filter.affectedMin);
    this.affectedMax = new NumberStore(filter.affectedMax);
    this.incidentsMin = new NumberStore(filter.incidentsMin);
    this.incidentsMax = new NumberStore(filter.incidentsMax);

    this.kinds = new KindInputStore(filter);
    this.devices = new DeviceInputStore(filter, hosts);

    this.cleanStartDateTime = reaction(
      () => this.startDateTime,
      () => {
        const startDateTime = this.startDateTime;
        const endDateTime = this.endDateTime;
        if (endDateTime < startDateTime) {
          this.dateRange.first.setDate(new Date(
            endDateTime.getFullYear(),
            endDateTime.getMonth(),
            endDateTime.getDate()
          ));
          this.timeStart.setTime(new Time(
            endDateTime.getHours(),
            endDateTime.getMinutes(),
            endDateTime.getSeconds()
          ));
        }
      },
      {
        fireImmediately: true,
      },
    );

    this.cleanEndDateTime = reaction(
      () => this.endDateTime,
      () => {
        const startDateTime = this.startDateTime;
        const endDateTime = this.endDateTime;
        if (endDateTime < startDateTime) {
          this.dateRange.final.setDate(new Date(
            startDateTime.getFullYear(),
            startDateTime.getMonth(),
            startDateTime.getDate()
          ));
          this.timeEnd.setTime(new Time(
            startDateTime.getHours(),
            startDateTime.getMinutes(),
            startDateTime.getSeconds()
          ));
        }
      },
      {
        fireImmediately: true,
      },
    );
  }

  clean(): void {
    this.cleanStartDateTime();
    this.cleanEndDateTime();
  }

  @computed get startDateTime(): Date {
    const dateStart = this.dateRange.first.committedDateObject;
    const timeStart = this.timeStart.committedTime;
    const startDateTime = new Date(
      dateStart.getFullYear(),
      dateStart.getMonth(),
      dateStart.getDate(),
      timeStart.hours,
      timeStart.minutes,
      timeStart.seconds
    );
    return startDateTime;
  }

  @computed get endDateTime(): Date {
    const dateEnd = this.dateRange.final.committedDateObject;
    const timeEnd = this.timeEnd.committedTime;
    const endDateTime = new Date(
      dateEnd.getFullYear(),
      dateEnd.getMonth(),
      dateEnd.getDate(),
      timeEnd.hours,
      timeEnd.minutes,
      timeEnd.seconds
    );
    return endDateTime;
  }

  @action.bound
  validate(filter: IssuesFilter): IssuesFilterApplyResult {
    let valid = true;

    const seen = [
      filter.seen.seen,
      filter.seen.unseen,
    ];
    if (!seen.some((value) => value)) {
      this.seenError = Messages.invalidSeen();
      valid = false;
    }

    if (!isNaN(filter.affectedMin) && filter.affectedMin < 0) {
      this.affectedError = Messages.errorMinLessThanZero();
      valid = false;
    } else if (!isNaN(filter.affectedMax) && filter.affectedMax < 0) {
      this.affectedError = Messages.errorMaxLessThanZero();
      valid = false;
    } else if (!isNaN(filter.affectedMin) && !isNaN(filter.affectedMax) &&
               filter.affectedMax < filter.affectedMin) {
      this.affectedError = Messages.errorMaxLessThanMin();
      valid = false;
    }

    if (!isNaN(filter.incidentsMin) && filter.incidentsMin < 0) {
      this.incidentsError = Messages.errorMinLessThanZero();
      valid = false;
    } else if (!isNaN(filter.incidentsMax) && filter.incidentsMax < 0) {
      this.incidentsError = Messages.errorMaxLessThanZero();
      valid = false;
    } else if (!isNaN(filter.incidentsMin) && !isNaN(filter.incidentsMax) &&
               filter.incidentsMax < filter.incidentsMin) {
      this.incidentsError = Messages.errorMaxLessThanMin();
      valid = false;
    }

    if (valid) {
      return { kind: ResultKind.OK, filter };
    } else {
      return {
        kind: ResultKind.USER_ERROR,
        seenError: this.seenError,
        affectedError: this.affectedError,
        incidentsError: this.incidentsError,
      };
    }
  }

  convertToIssuesFilter(): IssuesFilter {
    const startDateTime = this.startDateTime;
    const endDateTime = this.endDateTime;
    return {
      search: this.search.value,
      start: startDateTime, end: endDateTime,
      seen: { ...this.seen.values },
      severityMin: this.severityMin.value,
      severityMax: this.severityMax.value,
      affectedMin: this.affectedMin.value,
      affectedMax: this.affectedMax.value,
      incidentsMin: this.incidentsMin.value,
      incidentsMax: this.incidentsMax.value,
      kinds: this.kinds.kinds,
      devices: this.devices.hostIDs,
    };
  }

  @action.bound
  handleApply(domain: IPangolinDomain): IssuesFilterApplyResult {
    this.seenError = undefined;
    this.affectedError = undefined;
    this.incidentsError = undefined;

    const filter = this.convertToIssuesFilter();

    const localResult = this.validate(filter);
    if (localResult.kind === ResultKind.USER_ERROR) {
      return localResult;
    }

    domain.issuesFilter = filter;
    domain.issuesFiltered = false;
    IssuesService.clearData(domain);

    return { kind: ResultKind.OK, filter };
  }
}
